import { useEffect } from 'react';
import Mousetrap from 'mousetrap';
import { useToolPreferences } from 'hooks/usePreferences';
import type { WithApolloContext } from 'modules/Apollo/types';
import { bindMousetrap } from 'utils/mousetraps';

/**
 * Handlers keyed by keyboard-shortcut id (the ids listed in `DEFAULT_KEYBOARD_SHORTCUTS`). A handler
 * may return `false` to decline the key press, which leaves the event unhandled so the browser's own
 * behaviour still applies -- used for actions that are conditionally disabled (e.g. saving a step
 * that has no unsaved changes).
 */
export type HPActionShortcutHandlers = Record<string, () => void | boolean>;

/**
 * Binds hanging-protocol action shortcuts to the keys the user configured in Keyboard Settings.
 *
 * These actions live on components rather than in `KeyboardShortcuts/viewer.ts` because they operate
 * on state owned by those components (the HP widget's menu/modal state, the step bar's active step).
 * Binding here also scopes them correctly for free: the HP widget only mounts on viewer/0, and the
 * step bar only mounts when the active protocol has steps.
 *
 * Uses a scoped Mousetrap instance (own binding table, still listening on `document`) so cleanup can
 * `reset()` just these shortcuts without clobbering other features bound to the same combo on the
 * global Mousetrap singleton -- the same reason `KeyboardShortcuts/viewer.ts` does it for step
 * navigation.
 *
 * `handlers` is a dependency of the binding effect -- memoize it at the call site so the bindings
 * aren't torn down and rebuilt on every render.
 */
export function useHPActionShortcuts(
  handlers: HPActionShortcutHandlers,
  { context }: WithApolloContext
): void {
  const [toolPreferences] = useToolPreferences({ context });
  const shortcuts = toolPreferences?.keyboard?.shortcuts;

  useEffect(() => {
    if (shortcuts == null) return;

    const hpActionMousetrap = new Mousetrap(document.documentElement);

    shortcuts.forEach(({ id, key }) => {
      if (typeof key !== 'string' || key === '') return;
      const handler = handlers[id];
      if (handler == null) return;

      bindMousetrap(hpActionMousetrap, key, (evt) => {
        // Run the handler first: a disabled action returns false and must not swallow the key.
        if (handler() === false) return;
        evt.preventDefault();
      });
    });

    return () => {
      hpActionMousetrap.reset();
    };
  }, [shortcuts, handlers]);
}
